import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, Package,
  ArrowLeft, Loader2, XCircle,
  Info, FileText, X, Plus, Search, Upload, UploadCloud, GitCompare, Unlink, Calculator
} from 'lucide-react';
import { incomingService, productService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import './IncomingValidationPage.css';

interface ValidationItem {
  productId: number;
  productName: string;
  requisitionQuantity: number;
  xmlQuantity: number;
  difference: number;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  differences: ValidationItem[];
}

interface InvoiceEntryItem {
  id: number;
  product_id: number;
  product_name?: string;
  sku?: string;
  product_barcode?: string;
  product_references?: string[];
  quantity: number;
  unit_price: number;
  total_price: number;
  supplier_code?: string;
  xml_barcode?: string;
  xml_description?: string;
  xml_unit?: string;
}

interface RequisitionItem {
  id: number;
  product_id: number;
  product_name_from_catalog?: string;
  product_sku?: string;
  requested_qty?: number; // Campo correto da tabela purchase_requisition_items
  requestedQty?: number; // Possível variação camelCase
  quantity?: number; // Alias opcional
  qty?: number; // Outra possível variação
  unit_price?: number;
  total_price?: number;
  supplier_code?: string;
}

interface InvoiceEntry {
  id: number;
  number: string;
  supplier_id: number;
  supplier_name?: string;
  status: string;
  validation?: ValidationResult;
  items?: InvoiceEntryItem[];
  requisitionItems?: RequisitionItem[];
  // Campos adicionais vindos do backend para controle de fluxo
  inspection_status?: string; // 'pending' | 'completed' etc. (snake_case do banco)
  skip_inspection?: boolean; // Flag para indicar se a conferência pode ser pulada
}

type XmlItemValidationStatus = 'unlinked' | 'ok' | 'qty_mismatch' | 'not_in_requisition';

function getRequisitionQty(item?: RequisitionItem): number {
  return Number(
    item?.requested_qty ?? item?.requestedQty ?? item?.quantity ?? item?.qty ?? 0
  );
}

function normalizeCode(value?: string | null): string {
  if (!value) return '';
  return value.toString().trim().replace(/\s+/g, '').toUpperCase();
}

function isValidXmlBarcode(barcode?: string | null): boolean {
  if (!barcode) return false;
  const value = barcode.toString().trim();
  if (!value || value.toUpperCase() === 'SEM GTIN') return false;
  if (/^0+$/.test(value)) return false;
  return true;
}

function formatXmlEan(barcode?: string | null): string {
  if (!isValidXmlBarcode(barcode)) return 'N/A';
  return barcode!.toString().trim();
}

type ProductLinkIssue = 'barcode_mismatch' | 'reference_mismatch' | 'no_reference_registered';

function getProductLinkIssue(xmlItem: InvoiceEntryItem): ProductLinkIssue | null {
  if (!xmlItem.product_id) return null;

  const xmlBarcode = xmlItem.xml_barcode?.trim();
  const productBarcode = xmlItem.product_barcode?.trim();

  // Prioridade 1: cEAN do XML vs código de barras do cadastro
  if (isValidXmlBarcode(xmlBarcode) && productBarcode) {
    return xmlBarcode === productBarcode ? null : 'barcode_mismatch';
  }

  // Prioridade 2: cProd vs referências do produto (não comparar com SKU interno)
  const cProd = xmlItem.supplier_code?.trim();
  if (!cProd) return null;

  const refs = xmlItem.product_references || [];
  if (refs.length === 0) return 'no_reference_registered';

  const refMatches = refs.some((ref) => normalizeCode(ref) === normalizeCode(cProd));
  return refMatches ? null : 'reference_mismatch';
}

function hasSuspiciousProductLink(xmlItem: InvoiceEntryItem): boolean {
  return getProductLinkIssue(xmlItem) !== null;
}

function getLinkModalInitialSearch(type: 'xml' | 'requisition', item: any): string {
  if (type === 'xml') {
    if (isValidXmlBarcode(item?.xml_barcode)) {
      return item.xml_barcode.trim();
    }
    if (item?.supplier_code?.trim()) {
      return item.supplier_code.trim();
    }
    return '';
  }

  return item?.product_name_from_catalog || item?.product_name || item?.product_sku || '';
}

function getProductLinkWarningMessage(xmlItem: InvoiceEntryItem): string {
  const issue = getProductLinkIssue(xmlItem);
  const refs = xmlItem.product_references || [];
  const refsLabel = refs.length > 0 ? refs.join(', ') : 'nenhuma';

  switch (issue) {
    case 'barcode_mismatch':
      return `cEAN do XML (${xmlItem.xml_barcode}) difere do código de barras cadastrado (${xmlItem.product_barcode}). Verifique o vínculo.`;
    case 'reference_mismatch':
      return `cProd (${xmlItem.supplier_code}) não consta nas referências do produto (${refsLabel}). Cadastre a referência ou corrija o vínculo.`;
    case 'no_reference_registered':
      return `Produto sem referência cadastrada para o cProd (${xmlItem.supplier_code}). Cadastre a referência do fornecedor no produto.`;
    default:
      return '';
  }
}

function evaluateXmlItemValidation(
  xmlItem: InvoiceEntryItem,
  requisitionItem?: RequisitionItem
) {
  const isRealXmlItem = Boolean(xmlItem?.id && xmlItem.id > 0);
  const hasSystemMatch = xmlItem?.product_id != null;
  const xmlQty = Number(xmlItem?.quantity ?? 0);
  const hasRequisitionMatch = Boolean(requisitionItem);
  const requisitionQty = getRequisitionQty(requisitionItem);
  const qtyMatch = !hasRequisitionMatch || Math.abs(requisitionQty - xmlQty) < 0.01;

  if (!isRealXmlItem) {
    return {
      isRealXmlItem: false,
      hasSystemMatch: false,
      hasRequisitionMatch: false,
      qtyMatch: true,
      rowOk: false,
      blocksApproval: false,
      status: 'unlinked' as XmlItemValidationStatus,
      xmlQty: 0,
      requisitionQty: 0
    };
  }

  if (!hasSystemMatch) {
    return {
      isRealXmlItem,
      hasSystemMatch: false,
      hasRequisitionMatch,
      qtyMatch,
      rowOk: false,
      blocksApproval: true,
      status: 'unlinked' as XmlItemValidationStatus,
      xmlQty,
      requisitionQty
    };
  }

  if (!hasRequisitionMatch) {
    return {
      isRealXmlItem,
      hasSystemMatch: true,
      hasRequisitionMatch: false,
      qtyMatch: true,
      rowOk: true,
      blocksApproval: false,
      status: 'not_in_requisition' as XmlItemValidationStatus,
      xmlQty,
      requisitionQty: 0
    };
  }

  if (!qtyMatch) {
    return {
      isRealXmlItem,
      hasSystemMatch: true,
      hasRequisitionMatch: true,
      qtyMatch: false,
      rowOk: false,
      blocksApproval: false,
      status: 'qty_mismatch' as XmlItemValidationStatus,
      xmlQty,
      requisitionQty
    };
  }

  return {
    isRealXmlItem,
    hasSystemMatch: true,
    hasRequisitionMatch: true,
    qtyMatch: true,
    rowOk: true,
    blocksApproval: false,
    status: 'ok' as XmlItemValidationStatus,
    xmlQty,
    requisitionQty
  };
}

const IncomingValidationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { hasPermission } = usePermissions();
  const canCancelIncoming = hasPermission(MODULES.STOCK, ACTIONS.STOCK_INCOMING_CANCEL);
  const [invoiceEntry, setInvoiceEntry] = useState<InvoiceEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  
  // Estados para modal de vinculação
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingItem, setLinkingItem] = useState<{type: 'xml' | 'requisition', item: any, productId?: number} | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedWarnings, setExpandedWarnings] = useState<Set<string>>(new Set());
  
  // Estados para upload de XML
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Estados para criação de produto
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [creatingProductItem, setCreatingProductItem] = useState<InvoiceEntryItem | null>(null);
  const [g1Email, setG1Email] = useState('');
  const [g1Password, setG1Password] = useState('');
  const [productName, setProductName] = useState('');
  const [productBarcode, setProductBarcode] = useState('');
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  
  // Estados para conversão de quantidade
  const [showConvertQuantityModal, setShowConvertQuantityModal] = useState(false);
  const [convertingItem, setConvertingItem] = useState<InvoiceEntryItem | null>(null);
  const [conversionType, setConversionType] = useState<'multiply' | 'divide'>('multiply');
  const [conversionValue, setConversionValue] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  // Modal de confirmação (desvinculação)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'danger';
    onConfirm: () => void;
  } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  const fetchProductsForLink = useCallback(async (term: string) => {
    const normalizedTerm = term.trim();
    if (normalizedTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await productService.getAll({ search: normalizedTerm, active: true });
      if (response?.success) {
        setSearchResults((response.data || []).slice(0, 10));
      } else {
        setSearchResults([]);
        showToast(response?.message || 'Erro ao buscar produtos', 'error');
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setSearchResults([]);
      showToast('Erro ao buscar produtos', 'error');
    } finally {
      setSearchLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (id) {
      loadInvoiceEntry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Buscar produtos quando o termo de busca mudar
  useEffect(() => {
    if (showLinkModal && searchTerm) {
      const timer = setTimeout(() => {
        fetchProductsForLink(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, showLinkModal, fetchProductsForLink]);

  const loadInvoiceEntry = async () => {
    try {
      setLoading(true);
      const response = await incomingService.getById(parseInt(id!));
      if (response.success) {
        console.log('📦 [IncomingValidationPage] Dados recebidos:', {
          hasItems: !!response.data.items,
          itemsCount: response.data.items?.length || 0,
          hasRequisitionItems: !!response.data.requisitionItems,
          requisitionItemsCount: response.data.requisitionItems?.length || 0,
          observation: response.data.observation,
          firstRequisitionItem: response.data.requisitionItems?.[0] || null,
          firstXmlItem: response.data.items?.[0] || null
        });
        
        // Debug: Verificar estrutura dos itens
        console.log('🔍 [IncomingValidationPage] Itens recebidos:', {
          requisitionItemsCount: response.data.requisitionItems?.length || 0,
          xmlItemsCount: response.data.items?.length || 0,
          requisitionItems: response.data.requisitionItems?.map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name_from_catalog,
            requested_qty: item.requested_qty
          })),
          xmlItems: response.data.items?.map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            xml_description: item.xml_description,
            supplier_code: item.supplier_code,
            quantity: item.quantity
          }))
        });
        
        setInvoiceEntry(response.data);
        setValidation(response.data.validation || null);
      } else {
        showToast('Nota fiscal não encontrada', 'error');
        navigate('/erp/stock/incoming');
      }
    } catch (error: any) {
      console.error('Erro ao carregar invoice entry:', error);
      showToast('Erro ao carregar nota fiscal', 'error');
      navigate('/erp/stock/incoming');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      if (!id) return;

      setConfirming(true);

      const alreadyInspected =
        invoiceEntry?.inspection_status === 'completed' ||
        // fallback caso backend venha com camelCase em algum momento
        // @ts-ignore
        (invoiceEntry as any)?.inspectionStatus === 'completed';

      let response;

      if (alreadyInspected) {
        // Nota já foi conferida anteriormente: pular etapa de conferência
        // e ir direto para "Aguard. Lançamento"
        response = await incomingService.updateStatus(parseInt(id), 'awaiting_launch');
      } else {
        // Fluxo normal: enviar para conferência (inspeção)
        response = await incomingService.updateStatus(parseInt(id), 'inspection', {
          inspectionStatus: 'pending'
        });
      }

      if (response.success) {
        showToast(
          alreadyInspected
            ? 'Validação confirmada! Nota pronta para lançamento.'
            : 'Validação confirmada! Nota enviada para conferência.',
          'success'
        );
        // Sempre redirecionar para o dashboard após aprovar validação
        navigate('/erp/stock/incoming');
      } else {
        showToast(response.message || 'Erro ao confirmar validação', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao confirmar validação:', error);
      const apiMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        'Erro ao confirmar validação';
      showToast(apiMessage, 'error');
    } finally {
      setConfirming(false);
    }
  };

  const canShowCancelButton =
    canCancelIncoming &&
    !!invoiceEntry &&
    ['pending', 'processed', 'inspection', 'awaiting_launch'].includes(
      invoiceEntry.status
    ) &&
    // @ts-ignore launched_status pode vir do backend
    (invoiceEntry as any).launched_status !== 'launched';

  const handleCancelEntryClick = () => {
    if (!id || !invoiceEntry) return;

    setConfirmModalConfig({
      title: 'Cancelar esta entrada?',
      message:
        `A nota #${invoiceEntry.number || id} será marcada como cancelada. ` +
        'O XML e os itens permanecem no histórico, mas esta entrada sai do fluxo operacional. ' +
        'Esta ação não estorna estoque (válida apenas antes do lançamento).',
      type: 'danger',
      onConfirm: async () => {
        setCancelling(true);
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
        try {
          const response = await incomingService.cancel(
            parseInt(id, 10),
            'Cancelado na tela de validação (XML incorreto)'
          );
          if (response.success) {
            showToast('Entrada cancelada com sucesso.', 'success');
            navigate('/erp/stock/incoming');
          } else {
            showToast(response.message || 'Erro ao cancelar entrada', 'error');
          }
        } catch (error: any) {
          console.error('Erro ao cancelar entrada:', error);
          showToast(
            error.response?.data?.message || 'Erro ao cancelar entrada',
            'error'
          );
        } finally {
          setCancelling(false);
        }
      },
    });
    setShowConfirmModal(true);
  };

  const handleBack = () => {
    navigate('/erp/stock/incoming');
  };

  const handleOpenCreateProductModal = (xmlItem: InvoiceEntryItem) => {
    setCreatingProductItem(xmlItem);
    setProductName(xmlItem.xml_description || '');
    setProductBarcode(xmlItem.xml_barcode || '');
    setG1Email('');
    setG1Password('');
    setShowCreateProductModal(true);
  };

  const handleVerifyG1Password = async () => {
    if (!g1Email || !g1Password) {
      showToast('Por favor, informe email e senha do usuário', 'error');
      return;
    }

    setIsVerifyingPassword(true);
    try {
      const response = await incomingService.verifyG1Password(g1Email, g1Password);
      if (response.success) {
        showToast('Autorização confirmada! Preencha os dados do produto.', 'success');
      } else {
        showToast(response.message || 'Erro ao verificar autorização', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao verificar senha G.1:', error);
      // Extrair mensagem de erro da resposta
      // O erro pode estar em error.response.data.message ou error.response.data
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || 
                          (typeof errorData === 'string' ? errorData : null) ||
                          error.message || 
                          'Erro ao verificar autorização. Verifique se o email e senha estão corretos.';
      showToast(errorMessage, 'error');
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!creatingProductItem || !id) return;

    if (!g1Email || !g1Password) {
      showToast('Por favor, informe email e senha do usuário', 'error');
      return;
    }

    if (!productName) {
      showToast('Nome do produto é obrigatório', 'error');
      return;
    }

    setIsCreatingProduct(true);
    try {
      const productData = {
        name: productName,
        barcode: productBarcode || null,
        description: creatingProductItem.xml_description || null,
        groupId: null,
        brandId: null,
        unitId: null
      };

      const response = await incomingService.createProductFromXmlItem(
        parseInt(id),
        creatingProductItem.id,
        g1Email,
        g1Password,
        productData
      );

      if (response.success) {
        showToast('Produto criado e vinculado com sucesso!', 'success');
        setShowCreateProductModal(false);
        setCreatingProductItem(null);
        setG1Email('');
        setG1Password('');
        setProductName('');
        setProductBarcode('');
        setTimeout(() => {
          loadInvoiceEntry();
        }, 500);
      } else {
        showToast(response.message || 'Erro ao criar produto', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao criar produto:', error);
      // Extrair mensagem de erro da resposta
      // O erro pode estar em error.response.data.message ou error.response.data
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || 
                          (typeof errorData === 'string' ? errorData : null) ||
                          error.message || 
                          'Erro ao criar produto. Verifique se o email e senha do G.1 estão corretos.';
      showToast(errorMessage, 'error');
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const handleOpenLinkModal = (type: 'xml' | 'requisition', item: any, productId?: number) => {
    const initialSearch = getLinkModalInitialSearch(type, item);

    setLinkingItem({ type, item, productId });
    setShowLinkModal(true);
    setSearchTerm(initialSearch);
    setSearchResults([]);

    if (initialSearch.trim().length >= 2) {
      fetchProductsForLink(initialSearch.trim());
    }
  };

  const handleOpenCreateFromLinkModal = () => {
    if (!linkingItem || linkingItem.type !== 'xml') {
      showToast('Criação de produto disponível apenas para itens do XML', 'warning');
      return;
    }

    const xmlItem = linkingItem.item as InvoiceEntryItem;
    setShowLinkModal(false);
    setLinkingItem(null);
    setSearchTerm('');
    setSearchResults([]);
    handleOpenCreateProductModal(xmlItem);
  };

  const handleLinkProduct = async (product: any) => {
    if (!linkingItem || !id) {
      showToast('Não foi possível identificar o item para vincular', 'error');
      return;
    }

    if (!product?.id) {
      showToast('Produto inválido para vinculação', 'error');
      return;
    }

    const itemId = linkingItem.item?.id;
    if (!itemId) {
      showToast('Item do XML/requisição sem identificador válido', 'error');
      return;
    }

    if (isLinking) return;

    setIsLinking(true);
    try {
      const response = await incomingService.linkItem(
        parseInt(id),
        linkingItem.type,
        itemId,
        product.id
      );

      if (response.success) {
        showToast(`Produto "${product.name}" vinculado com sucesso!`, 'success');
        setShowLinkModal(false);
        setLinkingItem(null);
        setSearchTerm('');
        setSearchResults([]);
        await loadInvoiceEntry();
      } else {
        showToast(response.message || 'Erro ao vincular produto', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao vincular produto:', error);
      showToast(
        error.response?.data?.message || 'Erro ao vincular produto',
        'error'
      );
    } finally {
      setIsLinking(false);
    }
  };

  const handleLinkSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (searchResults.length > 0 && !isLinking) {
      handleLinkProduct(searchResults[0]);
    }
  };

  // Função para abrir modal de conversão de quantidade
  const handleOpenConvertQuantityModal = (xmlItem: InvoiceEntryItem) => {
    setConvertingItem(xmlItem);
    setConversionType('multiply');
    setConversionValue('');
    setShowConvertQuantityModal(true);
  };

  // Função para converter quantidade
  const handleConvertQuantity = async () => {
    if (!convertingItem || !id || !conversionValue) {
      showToast('Preencha o valor de conversão', 'error');
      return;
    }

    const value = parseFloat(conversionValue);
    if (isNaN(value) || value <= 0) {
      showToast('O valor deve ser um número positivo', 'error');
      return;
    }

    setIsConverting(true);
    try {
      const data = conversionType === 'multiply' 
        ? { multiplier: value }
        : { divisor: value };

      const response = await incomingService.convertItemQuantity(
        parseInt(id),
        convertingItem.id,
        data
      );

      if (response.success) {
        const currentQty = Number(convertingItem.quantity || 0);
        const newQty = conversionType === 'multiply' 
          ? currentQty * value 
          : currentQty / value;
        
        showToast(
          `Quantidade convertida: ${currentQty.toFixed(2)} → ${newQty.toFixed(2)}`, 
          'success'
        );
        setShowConvertQuantityModal(false);
        setConvertingItem(null);
        setConversionValue('');
        
        // Recarregar dados após conversão
        setTimeout(() => {
          loadInvoiceEntry();
        }, 500);
      } else {
        showToast(response.message || 'Erro ao converter quantidade', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao converter quantidade:', error);
      showToast(error.response?.data?.message || 'Erro ao converter quantidade', 'error');
    } finally {
      setIsConverting(false);
    }
  };

  // Função para desvincular produto
  const handleUnlinkProduct = (xmlItem: InvoiceEntryItem) => {
    if (!xmlItem || !id) return;

    const linkedSku = xmlItem.sku || 'N/A';
    const linkedRefs = (xmlItem.product_references || []).join(', ') || 'nenhuma';
    const supplierCode = xmlItem.supplier_code || 'Não informado';
    const xmlBarcode = xmlItem.xml_barcode || 'Não informado';
    const linkedName = xmlItem.product_name || `Produto #${xmlItem.product_id}`;
    const xmlDescription = xmlItem.xml_description || xmlItem.product_name || 'N/A';

    setConfirmModalConfig({
      title: 'Desvincular Item',
      message:
        `Desvincular este item do cadastro?\n\n` +
        `XML: ${xmlDescription}\n` +
        `cProd: ${supplierCode}\n` +
        `cEAN: ${xmlBarcode}\n\n` +
        `Vinculado a: #${xmlItem.product_id} — ${linkedName}\n` +
        `SKU interno: ${linkedSku}\n` +
        `Referências: ${linkedRefs}\n\n` +
        `Após desvincular, o sistema não revinculará automaticamente este item.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          setIsUnlinking(true);
          const response = await incomingService.unlinkItem(
            parseInt(id!),
            xmlItem.id
          );

          if (response.success) {
            showToast('Item desvinculado com sucesso!', 'success');
            setShowConfirmModal(false);
            setConfirmModalConfig(null);
            await loadInvoiceEntry();
          } else {
            showToast(response.message || 'Erro ao desvincular item', 'error');
          }
        } catch (error: any) {
          console.error('Erro ao desvincular item:', error);
          showToast('Erro ao desvincular item', 'error');
        } finally {
          setIsUnlinking(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  // Função para toggle de avisos expandidos
  const toggleWarning = (key: string) => {
    setExpandedWarnings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };


  // Funções para upload de XML
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'application/xml' || file.type === 'text/xml' || file.name.endsWith('.xml')) {
        setSelectedFile(file);
      } else {
        showToast('Por favor, selecione um arquivo XML', 'error');
      }
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !id) return;

    setIsUploading(true);
    try {
      const response = await incomingService.uploadXml(id, selectedFile);
      if (response.success) {
        showToast('XML reenviado com sucesso! Recarregando dados...', 'success');
        setShowUploadModal(false);
        setSelectedFile(null);
        // Recarregar dados após upload
        setTimeout(() => {
          loadInvoiceEntry();
        }, 1000);
      } else {
        showToast(response.message || 'Erro ao reenviar XML', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      showToast(error.response?.data?.message || 'Erro ao fazer upload do XML', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="incoming-validation-loading">
        <Loader2 size={32} className="incoming-validation-spinner" />
        <p>Carregando dados de validação...</p>
      </div>
    );
  }

  if (!invoiceEntry) {
    return null;
  }

  // Verificar se há itens que existem apenas em um lado (requisição ou XML)
  const hasMissingItems = (() => {
    if (!invoiceEntry?.requisitionItems || !invoiceEntry?.items) return false;
    
    const requisitionProductIds = new Set(
      invoiceEntry.requisitionItems
        .filter(item => item && item.product_id != null)
        .map(item => item.product_id)
    );
    const xmlProductIds = new Set(
      invoiceEntry.items
        .filter(item => item && item.product_id != null)
        .map(item => item.product_id)
    );
    
    // Verificar se há itens no XML que não estão na requisição
    const itemsOnlyInXml = Array.from(xmlProductIds).filter(id => !requisitionProductIds.has(id));
    // Verificar se há itens na requisição que não estão no XML
    const itemsOnlyInRequisition = Array.from(requisitionProductIds).filter(id => !xmlProductIds.has(id));
    
    return itemsOnlyInXml.length > 0 || itemsOnlyInRequisition.length > 0;
  })();

  const hasDifferences = validation?.differences && Array.isArray(validation.differences) && validation.differences.length > 0;
  const hasWarnings = validation?.warnings && Array.isArray(validation.warnings) && validation.warnings.length > 0;
  const hasErrors = validation?.errors && Array.isArray(validation.errors) && validation.errors.length > 0;
  
  const requisitionItemsList = invoiceEntry.requisitionItems || [];
  const requisitionMapByProductId = new Map<number, RequisitionItem>(
    requisitionItemsList
      .filter((item) => item && item.product_id != null)
      .map((item) => [item.product_id, item])
  );

  const xmlItemsList = (invoiceEntry.items || []).filter((item) => item && item.id > 0);
  const xmlItemValidations = xmlItemsList.map((xmlItem) =>
    evaluateXmlItemValidation(
      xmlItem,
      xmlItem.product_id ? requisitionMapByProductId.get(xmlItem.product_id) : undefined
    )
  );

  // Bloqueia aprovação apenas se houver item do XML sem vínculo ao cadastro de produtos
  const hasBlockingDivergences = xmlItemValidations.some((v) => v.blocksApproval);
  const okItemsCount = xmlItemValidations.filter((v) => v.rowOk).length;
  const qtyMismatchCount = xmlItemValidations.filter((v) => v.status === 'qty_mismatch').length;

  const handleApproveClick = () => {
    if (hasBlockingIssues) {
      showToast(
        'Vincule todos os itens do XML a um produto do sistema antes de aprovar.',
        'warning'
      );
      return;
    }
    handleConfirm();
  };
  
  // Verificar se realmente há problemas para mostrar a mensagem de aviso
  const hasRealProblems = hasDifferences || hasWarnings || hasErrors || hasMissingItems;
  
  // Verificar se há bloqueios que impedem aprovação
  const hasBlockingIssues = hasErrors || hasBlockingDivergences;

  return (
    <div className="incoming-validation">
      {/* Tool Header */}
      <div className="incoming-validation-tool-header">
        <div className="incoming-validation-tool-header-left">
          <button 
            onClick={handleBack} 
            className="incoming-validation-tool-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="incoming-validation-tool-title-row">
              <h1 className="incoming-validation-tool-title">
                Validação XML {id}
              </h1>
              <span className="incoming-validation-tool-badge">Match XML vs Pedido</span>
            </div>
            <p className="incoming-validation-tool-subtitle">
              Verificação de Equivalência de Itens e Quantidades
            </p>
          </div>
        </div>

        <div className="incoming-validation-tool-header-right">
          {canShowCancelButton ? (
            <button
              type="button"
              onClick={handleCancelEntryClick}
              disabled={cancelling || confirming}
              className="incoming-validation-tool-cancel-btn"
              title="Cancelar esta entrada (XML incorreto)"
            >
              {cancelling ? (
                <>
                  <Loader2 size={16} className="incoming-validation-spinner" />
                  Cancelando...
                </>
              ) : (
                <>
                  <XCircle size={16} />
                  Cancelar entrada
                </>
              )}
            </button>
          ) : null}
          <button
            onClick={() => setShowUploadModal(true)}
            className="incoming-validation-tool-upload-btn"
            title="Refazer upload do XML"
          >
            <Upload size={18} />
            Reenviar XML
          </button>
          <button
            onClick={handleApproveClick}
            disabled={confirming}
            className={`incoming-validation-tool-finish-btn${hasBlockingIssues ? ' is-blocked' : ''}`}
            title={hasBlockingIssues ? 'Vincule todos os itens do XML antes de aprovar' : ''}
          >
            {confirming ? (
              <>
                <Loader2 size={16} className="incoming-validation-spinner" />
                Processando...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                {invoiceEntry?.inspection_status === 'completed'
                  ? 'Aprovar e Seguir para Lançamento'
                  : 'Aprovar e Seguir para Conferência'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="incoming-validation-main-grid">
        {/* Comparison Table */}
          <div>
          {(invoiceEntry.items && invoiceEntry.items.length > 0) ? (
            <div className="incoming-validation-comparison-section">
              <div className="incoming-validation-comparison-header">
                <h3>Comparativo de Itens</h3>
                <div className="incoming-validation-comparison-legend">
                  <div className="incoming-validation-legend-item">
                    <span className="incoming-validation-legend-dot match"></span>
                    <span className="incoming-validation-legend-text">Match OK</span>
                  </div>
                  <div className="incoming-validation-legend-item">
                    <span className="incoming-validation-legend-dot difference"></span>
                    <span className="incoming-validation-legend-text">Divergência</span>
                  </div>
          </div>
        </div>

              <div className="incoming-validation-table-wrapper">
                <table className="incoming-validation-comparison-table">
                  <thead>
                    <tr>
                      <th>Produto / SKU</th>
                      <th className="text-center">Qtd XML</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {xmlItemsList.map((xmlItem) => {
                      const productId = xmlItem.product_id;
                      const requisitionItem = productId
                        ? requisitionMapByProductId.get(productId)
                        : undefined;
                      const validation = evaluateXmlItemValidation(xmlItem, requisitionItem);
                      const systemProductName = xmlItem?.product_name || null;
                      const systemProductSku = xmlItem?.sku || null;
                      const suspiciousLink = hasSuspiciousProductLink(xmlItem);
                      const productName =
                        xmlItem?.xml_description ||
                        xmlItem?.product_name ||
                        `Produto ${productId ? `#${productId}` : 'não vinculado'}`;
                      const cProd = xmlItem?.supplier_code || 'N/A';
                      const cEan = formatXmlEan(xmlItem?.xml_barcode);
                      const isUnlinkedFromSystem = validation.status === 'unlinked';
                      const warningKey = isUnlinkedFromSystem
                        ? `system-unlinked-${xmlItem.id}`
                        : '';
                      const showWarning = isUnlinkedFromSystem || suspiciousLink;
                      const isExpanded = expandedWarnings.has(warningKey);

                      const statusTitle =
                        validation.status === 'unlinked'
                          ? 'Item não vinculado ao cadastro'
                          : validation.status === 'qty_mismatch'
                          ? `Divergência de quantidade: pedido ${validation.requisitionQty}, XML ${validation.xmlQty}`
                          : 'Item validado';

                      return (
                          <tr
                            key={`xml-${xmlItem.id}`}
                            className={showWarning ? 'incoming-validation-row-warning' : ''}
                          >
                            <td>
                              <div className="incoming-validation-table-product">
                                <div
                                  className={`incoming-validation-table-product-icon ${
                                    validation.rowOk ? 'match' : 'difference'
                                  }`}
                                >
                                  <Package size={20} />
                                </div>
                                <div className="incoming-validation-table-product-info">
                                  <p className="incoming-validation-table-product-name">{productName}</p>
                                  <p className="incoming-validation-table-product-sku">cProd: {cProd}</p>
                                  <p className="incoming-validation-table-product-sku">cEAN: {cEan}</p>
                                  {validation.hasSystemMatch && systemProductName && (
                                    <div
                                      className={`incoming-validation-table-product-link${
                                        suspiciousLink ? ' is-suspicious' : ''
                                      }`}
                                    >
                                      <GitCompare size={12} />
                                      <span>
                                        Vinculado a: #{productId} — {systemProductName}
                                        {systemProductSku ? ` (SKU: ${systemProductSku})` : ''}
                                      </span>
                                    </div>
                                  )}
                                  {suspiciousLink && (
                                    <p className="incoming-validation-table-product-link-warning">
                                      {getProductLinkWarningMessage(xmlItem)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="incoming-validation-table-qty">
                              {validation.isRealXmlItem ? (
                                <>
                                  <span>{validation.xmlQty > 0 ? validation.xmlQty.toFixed(0) : '-'}</span>
                                  {validation.hasRequisitionMatch && validation.requisitionQty > 0 && (
                                    <span className="incoming-validation-qty-ref">
                                      Pedido: {validation.requisitionQty.toFixed(0)}
                                    </span>
                                  )}
                                </>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="incoming-validation-table-status">
                              <div className="incoming-validation-status-container">
                                {validation.status === 'unlinked' ? (
                                  <div
                                    className={`incoming-validation-status-badge difference ${showWarning ? 'has-warning' : ''}`}
                                    onClick={() => showWarning && toggleWarning(warningKey)}
                                    style={{ cursor: showWarning ? 'pointer' : 'default' }}
                                    title={statusTitle}
                                  >
                                    <AlertTriangle size={16} strokeWidth={3} />
                                    {showWarning && (
                                      <div className={`incoming-validation-warning-dropdown ${isExpanded ? 'expanded' : ''}`}>
                                        <div className="incoming-validation-warning-content">
                                          <p>
                                            Este item do XML não está vinculado a um produto do cadastro.
                                            Vincule manualmente antes de aprovar a validação.
                                          </p>
                                          <div className="incoming-validation-warning-details">
                                            <p><strong>Informações do XML:</strong></p>
                                            <p>Código: {xmlItem.supplier_code || 'Não informado'}</p>
                                            <p>Descrição: {xmlItem.xml_description || xmlItem.product_name || 'N/A'}</p>
                                            <p>Quantidade: {validation.xmlQty.toFixed(2)}</p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div
                                    className={`incoming-validation-status-badge ${
                                      validation.rowOk ? 'match' : 'difference'
                                    }`}
                                    title={statusTitle}
                                  >
                                    {validation.rowOk ? (
                                      <CheckCircle2 size={16} strokeWidth={3} />
                                    ) : (
                                      <X size={16} strokeWidth={3} />
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="incoming-validation-table-actions">
                              <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                                {isUnlinkedFromSystem && (
                                  <>
                                    <button
                                      className="incoming-validation-link-btn"
                                      onClick={() => {
                                        if (xmlItem) {
                                          handleOpenLinkModal('xml', xmlItem, productId);
                                        }
                                      }}
                                      title="Vincular item a um produto existente"
                                    >
                                      <Search size={16} />
                                    </button>
                                    <button
                                      className="incoming-validation-create-btn"
                                      onClick={() => {
                                        if (xmlItem) {
                                          handleOpenCreateProductModal(xmlItem);
                                        }
                                      }}
                                      title="Criar novo produto (requer autorização)"
                                    >
                                      <Plus size={16} />
                                    </button>
                                  </>
                                )}
                                {validation.hasSystemMatch && !isUnlinkedFromSystem && (
                                  <button
                                    className="incoming-validation-unlink-btn"
                                    onClick={() => {
                                      if (xmlItem) {
                                        handleUnlinkProduct(xmlItem);
                                      }
                                    }}
                                    disabled={isUnlinking}
                                    title="Desvincular item do cadastro"
                                  >
                                    <Unlink size={16} />
                                  </button>
                                )}
                                {/* Botão de conversão de quantidade - sempre visível */}
                                <button
                                  className="incoming-validation-convert-btn"
                                  onClick={() => {
                                    if (xmlItem) {
                                      handleOpenConvertQuantityModal(xmlItem);
                                    }
                                  }}
                                  title="Converter quantidade (multiplicar ou dividir)"
                                >
                                  <Calculator size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="incoming-validation-section">
              <div className="incoming-validation-section-header">
                <Info size={20} />
                <h2>Informações</h2>
              </div>
              <div className="incoming-validation-info">
                <p>
                  {!invoiceEntry.items || invoiceEntry.items.length === 0
                    ? 'Esta nota fiscal não possui itens cadastrados. Faça o upload do XML para processar os itens.'
                    : 'Não há dados disponíveis para comparação.'}
                </p>
              </div>
            </div>
          )}

      {/* Errors */}
      {hasErrors && (
        <div className="incoming-validation-section error">
          <div className="incoming-validation-section-header">
            <XCircle size={20} />
            <h2>Erros Encontrados</h2>
          </div>
          <ul className="incoming-validation-list">
            {validation.errors.map((error, idx) => (
              <li key={idx} className="incoming-validation-error-item">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}


          {/* Info Box */}
          <div className={`incoming-validation-info ${hasRealProblems ? 'warning' : ''}`}>
            <Info size={18} />
            <p>
              {hasRealProblems
                ? hasMissingItems
                  ? 'Há itens que existem apenas no pedido ou apenas no XML. Revise a comparação acima antes de prosseguir.'
                  : hasDifferences || hasWarnings
                    ? 'Revise as divergências e avisos acima. Você pode confirmar mesmo com divergências - elas serão registradas para referência.'
                    : hasErrors
                      ? 'Há erros que precisam ser corrigidos antes de prosseguir.'
                      : 'Revise as informações acima antes de prosseguir.'
                : qtyMismatchCount > 0
                  ? 'Itens vinculados ao cadastro. Há divergência de quantidade entre pedido e XML — você pode aprovar e ajustar na conferência.'
                  : 'Todos os itens do XML estão vinculados ao cadastro. Você pode prosseguir para a conferência.'}
            </p>
          </div>
          </div>

        {/* Sidebar Summary */}
        <div className="incoming-validation-sidebar">
          <div className="incoming-validation-summary-card">
            <h3 className="incoming-validation-summary-title">
              <FileText size={16} />
              Resumo da Validação
            </h3>

            <div className="incoming-validation-summary-content">
              <div className="incoming-validation-summary-stats">
                <div className="incoming-validation-summary-stat">
                  <p className="incoming-validation-summary-stat-label">Itens OK</p>
                  <p className={`incoming-validation-summary-stat-value ok`}>
                    {okItemsCount}
                  </p>
                </div>
                <div className="incoming-validation-summary-stat">
                  <p className="incoming-validation-summary-stat-label">Divergências</p>
                  <p className={`incoming-validation-summary-stat-value difference`}>
                    {qtyMismatchCount + (validation?.differences?.length || 0) + (hasMissingItems ? 1 : 0)}
            </p>
          </div>
        </div>

              {hasRealProblems && (
                <div className="incoming-validation-summary-alert">
                  <div className="incoming-validation-summary-alert-content">
                    <AlertTriangle size={18} />
                    <p className="incoming-validation-summary-alert-text">
                      {hasMissingItems
                        ? 'Atenção: Há itens que existem apenas no pedido ou apenas no XML. O sistema sugere revisão antes de prosseguir.'
                        : hasDifferences
                        ? 'Atenção: Há divergências de quantidade entre o XML e o pedido. O sistema sugere aceitação parcial ou devolução.'
                        : 'Atenção: Revise as informações acima antes de prosseguir.'}
                    </p>
                  </div>
          </div>
              )}

              <div className="incoming-validation-summary-info">
                <div className="incoming-validation-summary-info-content">
                  <Info size={16} />
                  <p className="incoming-validation-summary-info-text">
                    A validação garante que o faturamento do fornecedor está em conformidade com o pedido de compra antes de iniciar a conferência física.
                  </p>
                              </div>
                                </div>
                                  </div>
                                  </div>
                              </div>
                            </div>

      {/* Errors */}
      {hasErrors && (
        <div className="incoming-validation-section error">
          <div className="incoming-validation-section-header">
            <XCircle size={20} />
            <h2>Erros Encontrados</h2>
          </div>
          <ul className="incoming-validation-list">
            {validation.errors.map((error, idx) => (
              <li key={idx} className="incoming-validation-error-item">
                {error}
              </li>
            ))}
          </ul>
                            </div>
                          )}




      {/* Modal de Vinculação */}
      {showLinkModal && linkingItem && (
        <div
          className="incoming-validation-modal-overlay"
          onClick={() => !isLinking && setShowLinkModal(false)}
        >
          <div className="incoming-validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-validation-modal-header">
              <h3>Vincular Produto</h3>
              <button
                type="button"
                className="incoming-validation-modal-close"
                onClick={() => !isLinking && setShowLinkModal(false)}
                disabled={isLinking}
              >
                <X size={20} />
              </button>
            </div>

            <div className="incoming-validation-modal-content">
              <div className="incoming-validation-modal-item-info">
                <p className="incoming-validation-modal-item-label">
                  {linkingItem.type === 'xml' ? 'Item do XML:' : 'Item da Requisição:'}
                </p>
                <p className="incoming-validation-modal-item-name">
                  {linkingItem.type === 'xml'
                    ? (linkingItem.item?.xml_description || linkingItem.item?.product_name || linkingItem.item?.description || 'N/A')
                    : (linkingItem.item?.product_name_from_catalog || linkingItem.item?.product_name || 'N/A')}
                </p>
                {linkingItem.type === 'xml' && (
                  <>
                    {linkingItem.item?.supplier_code && (
                      <p className="incoming-validation-modal-item-detail">
                        cProd: {linkingItem.item.supplier_code}
                      </p>
                    )}
                    {linkingItem.item?.xml_barcode && linkingItem.item.xml_barcode !== 'SEM GTIN' && (
                      <p className="incoming-validation-modal-item-detail">
                        cEAN: {linkingItem.item.xml_barcode}
                      </p>
                    )}
                    {linkingItem.item?.quantity && (
                      <p className="incoming-validation-modal-item-detail">
                        Quantidade: {Number(linkingItem.item.quantity).toFixed(2)}
                      </p>
                    )}
                  </>
                )}
                {linkingItem.type === 'requisition' && (
                  <>
                    {linkingItem.item?.product_sku && (
                      <p className="incoming-validation-modal-item-detail">
                        SKU: {linkingItem.item.product_sku}
                      </p>
                    )}
                    {linkingItem.item?.requested_qty && (
                      <p className="incoming-validation-modal-item-detail">
                        Quantidade: {Number(linkingItem.item.requested_qty).toFixed(2)}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="incoming-validation-modal-search">
                <div className="incoming-validation-modal-search-input-wrapper">
                  <Search size={20} className="incoming-validation-modal-search-icon" />
                  <input
                    type="text"
                    className="incoming-validation-modal-search-input"
                    placeholder="Buscar por nome, cEAN, cProd/referência ou SKU interno..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleLinkSearchKeyDown}
                    autoFocus
                    disabled={isLinking}
                  />
                  {searchLoading && <Loader2 size={20} className="incoming-validation-spinner" />}
                </div>

                {searchResults.length > 0 && (
                  <div className="incoming-validation-modal-results">
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        className="incoming-validation-modal-result-item"
                      >
                        <div className="incoming-validation-modal-result-info">
                          <p className="incoming-validation-modal-result-name">{product.name}</p>
                          <p className="incoming-validation-modal-result-sku">SKU interno: {product.sku || 'N/A'}</p>
                          {product.barcode && (
                            <p className="incoming-validation-modal-result-barcode">cEAN: {product.barcode}</p>
                          )}
                          {product.references && product.references.length > 0 && (
                            <p className="incoming-validation-modal-result-reference">
                              Referências (cProd): {product.references.join(', ')}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="incoming-validation-modal-result-action"
                          onClick={() => handleLinkProduct(product)}
                          disabled={isLinking}
                          title={`Vincular a ${product.name}`}
                        >
                          {isLinking ? (
                            <Loader2 size={18} className="incoming-validation-spinner" />
                          ) : (
                            <Plus size={20} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {searchTerm.trim().length >= 2 && !searchLoading && searchResults.length === 0 && (
                  <div className="incoming-validation-modal-no-results">
                    <p>Nenhum produto encontrado para &quot;{searchTerm.trim()}&quot;</p>
                    {linkingItem.type === 'xml' && (
                      <button
                        type="button"
                        className="incoming-validation-modal-btn-secondary incoming-validation-modal-no-results-action"
                        onClick={handleOpenCreateFromLinkModal}
                        disabled={isLinking}
                      >
                        <Plus size={16} />
                        Criar novo produto
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload de XML */}
      {showUploadModal && (
        <div className="incoming-validation-modal-overlay" onClick={() => !isUploading && setShowUploadModal(false)}>
          <div className="incoming-validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-validation-modal-header">
              <h3>Reenviar XML da Nota Fiscal</h3>
              <button 
                className="incoming-validation-modal-close"
                onClick={() => !isUploading && setShowUploadModal(false)}
                disabled={isUploading}
              >
                <X size={20} />
              </button>
          </div>
            
            <div className="incoming-validation-modal-content">
              <div className={`incoming-validation-upload-area ${isUploading ? 'uploading' : ''}`}>
                {isUploading ? (
                  <div className="incoming-validation-upload-loading">
                    <Loader2 size={40} className="incoming-validation-spinner" />
                    <p className="incoming-validation-upload-text">Processando NFe...</p>
                    <p className="incoming-validation-upload-subtext">Validando Assinatura Digital e Itens</p>
        </div>
      ) : (
                  <div className="incoming-validation-upload-content">
                    <div className="incoming-validation-upload-icon">
                      <UploadCloud size={32} />
          </div>
                    <p className="incoming-validation-upload-text">Arraste o arquivo XML aqui</p>
                    <p className="incoming-validation-upload-subtext">ou clique para selecionar do seu computador</p>
                    <input
                      type="file"
                      accept=".xml,application/xml,text/xml"
                      onChange={handleFileSelect}
                      className="incoming-validation-file-input"
                      id="xml-file-input-upload"
                    />
                    <label htmlFor="xml-file-input-upload" className="incoming-validation-file-label">
                      Selecionar Arquivo
                    </label>
                    {selectedFile && (
                      <div className="incoming-validation-file-selected">
                        <FileText size={16} />
                        <span>{selectedFile.name}</span>
                        <button
                          onClick={() => setSelectedFile(null)}
                          className="incoming-validation-file-remove"
                        >
                          <X size={14} />
                        </button>
          </div>
                    )}
                    {selectedFile && (
                      <button
                        onClick={handleFileUpload}
                        className="incoming-validation-upload-btn"
                        disabled={isUploading}
                      >
                        {isUploading ? 'Enviando...' : 'Enviar XML'}
                      </button>
                    )}
        </div>
      )}
              </div>

              <div className="incoming-validation-modal-info">
                <Info size={18} className="incoming-validation-info-icon" />
                <p className="incoming-validation-info-text">
                  Ao reenviar o XML, o sistema irá reprocessar todos os itens e atualizar os dados da nota fiscal. 
                  Os itens serão validados novamente contra a requisição de compra.
        </p>
      </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Produto */}
      {showCreateProductModal && creatingProductItem && (
        <div className="incoming-validation-modal-overlay" onClick={() => !isCreatingProduct && setShowCreateProductModal(false)}>
          <div className="incoming-validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-validation-modal-header">
              <h3>Criar Novo Produto</h3>
              <button 
                className="incoming-validation-modal-close"
                onClick={() => !isCreatingProduct && setShowCreateProductModal(false)}
                disabled={isCreatingProduct}
              >
                <X size={20} />
              </button>
            </div>

            <div className="incoming-validation-modal-content">
              <div className="incoming-validation-modal-item-info">
                <p className="incoming-validation-modal-item-label">Item do XML:</p>
                <p className="incoming-validation-modal-item-name">
                  {creatingProductItem.xml_description || creatingProductItem.product_name || 'N/A'}
                </p>
                {creatingProductItem.supplier_code && (
                  <p className="incoming-validation-modal-item-sku">
                    Código: {creatingProductItem.supplier_code}
                  </p>
                )}
              </div>

              <div className="incoming-validation-modal-section">
                <h4>Autorização</h4>
                <p className="incoming-validation-modal-info-text">
                  Para criar um novo produto, é necessário autorização de um usuário.
                </p>
                <div className="incoming-validation-modal-form-group">
                  <label>Email do usuário:</label>
                  <input
                    type="email"
                    value={g1Email}
                    onChange={(e) => setG1Email(e.target.value)}
                    placeholder="email@exemplo.com"
                    disabled={isCreatingProduct || isVerifyingPassword}
                  />
                </div>
                <div className="incoming-validation-modal-form-group">
                  <label>Senha:</label>
                  <input
                    type="password"
                    value={g1Password}
                    onChange={(e) => setG1Password(e.target.value)}
                    placeholder="Senha do usuário"
                    disabled={isCreatingProduct || isVerifyingPassword}
                  />
                </div>
                <button
                  className="incoming-validation-modal-btn-secondary"
                  onClick={handleVerifyG1Password}
                  disabled={isCreatingProduct || isVerifyingPassword || !g1Email || !g1Password}
                >
                  {isVerifyingPassword ? (
                    <>
                      <Loader2 size={14} className="incoming-validation-spinner" />
                      Verificando...
                    </>
                  ) : (
                    'Verificar Autorização'
                  )}
                </button>
              </div>

              <div className="incoming-validation-modal-section">
                <h4>Dados do Produto</h4>
                <div className="incoming-validation-modal-form-group">
                  <label>Nome do Produto *:</label>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Nome do produto"
                    disabled={isCreatingProduct}
                  />
                </div>
                <div className="incoming-validation-modal-form-group">
                  <label>Código de Barras:</label>
                  <input
                    type="text"
                    value={productBarcode}
                    onChange={(e) => setProductBarcode(e.target.value)}
                    placeholder="Código de barras (opcional)"
                    disabled={isCreatingProduct}
                  />
                </div>
              </div>

              <div className="incoming-validation-modal-actions">
                <button
                  type="button"
                  className="incoming-validation-modal-btn-primary"
                  onClick={() => {
                    if (!g1Email || !g1Password) {
                      showToast('Informe email e senha de um usuário G.1 para criar o produto', 'warning');
                      return;
                    }
                    if (!productName.trim()) {
                      showToast('Nome do produto é obrigatório', 'warning');
                      return;
                    }
                    handleCreateProduct();
                  }}
                  disabled={isCreatingProduct}
                >
                  {isCreatingProduct ? (
                    <>
                      <Loader2 size={14} className="incoming-validation-spinner" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Criar Produto
                    </>
                  )}
                </button>
                <button
                  className="incoming-validation-modal-btn-secondary"
                  onClick={() => setShowCreateProductModal(false)}
                  disabled={isCreatingProduct}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Conversão de Quantidade */}
      {showConvertQuantityModal && convertingItem && (
        <div className="incoming-validation-modal-overlay" onClick={() => !isConverting && setShowConvertQuantityModal(false)}>
          <div className="incoming-validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-validation-modal-header">
              <h3>Converter Quantidade</h3>
              <button 
                className="incoming-validation-modal-close"
                onClick={() => !isConverting && setShowConvertQuantityModal(false)}
                disabled={isConverting}
              >
                <X size={20} />
              </button>
            </div>

            <div className="incoming-validation-modal-content">
              <div className="incoming-validation-modal-item-info">
                <p className="incoming-validation-modal-item-label">Item:</p>
                <p className="incoming-validation-modal-item-name">
                  {convertingItem.xml_description || convertingItem.product_name || 'N/A'}
                </p>
                <p className="incoming-validation-modal-item-detail">
                  Quantidade atual: {Number(convertingItem.quantity || 0).toFixed(2)} {convertingItem.xml_unit || 'UN'}
                </p>
              </div>

              <div className="incoming-validation-modal-section">
                <h4>Tipo de Conversão</h4>
                <div className="incoming-validation-modal-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="radio"
                      name="conversionType"
                      value="multiply"
                      checked={conversionType === 'multiply'}
                      onChange={(e) => setConversionType(e.target.value as 'multiply' | 'divide')}
                      disabled={isConverting}
                    />
                    Multiplicar (ex: 1 caixa × 100 = 100 unidades)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="conversionType"
                      value="divide"
                      checked={conversionType === 'divide'}
                      onChange={(e) => setConversionType(e.target.value as 'multiply' | 'divide')}
                      disabled={isConverting}
                    />
                    Dividir (ex: 100 unidades ÷ 100 = 1 caixa)
                  </label>
                </div>
              </div>

              <div className="incoming-validation-modal-section">
                <h4>Valor</h4>
                <div className="incoming-validation-modal-form-group">
                  <label>
                    {conversionType === 'multiply' ? 'Multiplicador:' : 'Divisor:'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={conversionValue}
                    onChange={(e) => setConversionValue(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder={conversionType === 'multiply' ? 'Ex: 100' : 'Ex: 100'}
                    disabled={isConverting}
                    autoFocus
                    className="incoming-validation-convert-input"
                  />
                </div>
                {conversionValue && !isNaN(parseFloat(conversionValue)) && parseFloat(conversionValue) > 0 && (
                  <div className="incoming-validation-modal-preview" style={{ 
                    marginTop: '1rem', 
                    padding: '0.75rem', 
                    background: 'var(--bg-secondary)', 
                    borderRadius: 'var(--border-radius-lg)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      <strong>Resultado:</strong>{' '}
                      {Number(convertingItem.quantity || 0).toFixed(2)} {conversionType === 'multiply' ? '×' : '÷'} {parseFloat(conversionValue).toFixed(2)} ={' '}
                      <strong style={{ color: 'var(--color-primary)' }}>
                        {conversionType === 'multiply'
                          ? (Number(convertingItem.quantity || 0) * parseFloat(conversionValue)).toFixed(2)
                          : (Number(convertingItem.quantity || 0) / parseFloat(conversionValue)).toFixed(2)}
                      </strong>
                    </p>
                  </div>
                )}
              </div>

              <div className="incoming-validation-modal-actions">
                <button
                  className="incoming-validation-modal-btn-primary"
                  onClick={handleConvertQuantity}
                  disabled={isConverting || !conversionValue || isNaN(parseFloat(conversionValue)) || parseFloat(conversionValue) <= 0}
                >
                  {isConverting ? (
                    <>
                      <Loader2 size={14} className="incoming-validation-spinner" />
                      Convertendo...
                    </>
                  ) : (
                    <>
                      <Calculator size={16} />
                      Converter
                    </>
                  )}
                </button>
                <button
                  className="incoming-validation-modal-btn-secondary"
                  onClick={() => setShowConvertQuantityModal(false)}
                  disabled={isConverting}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && confirmModalConfig && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            if (isUnlinking) return;
            setShowConfirmModal(false);
            setConfirmModalConfig(null);
          }}
          onConfirm={confirmModalConfig.onConfirm}
          title={confirmModalConfig.title}
          message={confirmModalConfig.message}
          type={confirmModalConfig.type}
          confirmText={isUnlinking ? 'Desvinculando...' : 'Desvincular'}
          cancelText="Cancelar"
        />
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default IncomingValidationPage;
